import { useMemo } from 'react';
import {
  Layout as AntLayout,
  Menu,
  Dropdown,
  Breadcrumb,
  theme,
  Avatar,
  Space,
  Typography,
} from 'antd';
import {
  AppstoreOutlined,
  ApiOutlined,
  PictureOutlined,
  ShoppingCartOutlined,
  TagsOutlined,
  UserOutlined,
  GoldOutlined,
  NotificationOutlined,
  GiftOutlined,
  DashboardOutlined,
  LogoutOutlined,
  SettingOutlined,
  CloudServerOutlined,
  FileImageOutlined,
  KeyOutlined,
  BarcodeOutlined,
  FundViewOutlined,
  DeploymentUnitOutlined,
  ShopOutlined,
  ExperimentOutlined,
  AuditOutlined,
  CreditCardOutlined,
  CrownOutlined,
  ShoppingOutlined,
} from '@ant-design/icons';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';

const { Header, Sider, Content } = AntLayout;

interface NavLeaf {
  key: string;
  label: string;
  icon: React.ReactNode;
  to: string;
}

interface NavGroup {
  key: string;
  label: string;
  icon: React.ReactNode;
  children: NavLeaf[];
}

type NavNode = NavLeaf | NavGroup;

const isGroup = (n: NavNode): n is NavGroup => 'children' in n;

// 按功能模块分组的导航树。路由 (to) 不变——仅把原扁平菜单归入 6 大模块,
// 便于运营按职责找到对应工作台(内容生产 / 商业变现 / 交易履约 / 用户增长 / 平台配置)。
const navTree: NavNode[] = [
  { key: '/dashboard', label: '总览', icon: <DashboardOutlined />, to: '/dashboard' },
  {
    key: 'grp-content',
    label: '内容生产',
    icon: <PictureOutlined />,
    children: [
      { key: '/lora', label: 'LoRA 建模', icon: <ExperimentOutlined />, to: '/lora' },
      { key: '/suite-workflow', label: '底版生产', icon: <FileImageOutlined />, to: '/suite-workflow' },
      { key: '/content', label: '套系管理', icon: <PictureOutlined />, to: '/content' },
      { key: '/plates-library', label: '照片库', icon: <FileImageOutlined />, to: '/plates-library' },
      { key: '/moderation', label: '内容审核', icon: <AuditOutlined />, to: '/moderation' },
      { key: '/workflows', label: '工作流模板', icon: <ApiOutlined />, to: '/workflows' },
    ],
  },
  {
    key: 'grp-commerce',
    label: '商业变现',
    icon: <TagsOutlined />,
    children: [
      { key: '/skus', label: '商业 SKU', icon: <TagsOutlined />, to: '/skus' },
      { key: '/membership', label: '会员套餐', icon: <CrownOutlined />, to: '/membership' },
      { key: '/mall', label: '商城管理', icon: <ShoppingOutlined />, to: '/mall' },
      { key: '/catalog', label: '供应商目录', icon: <ShopOutlined />, to: '/catalog' },
      { key: '/cardkeys', label: '卡密库存', icon: <BarcodeOutlined />, to: '/cardkeys' },
      { key: '/coupons', label: '优惠券', icon: <GiftOutlined />, to: '/coupons' },
      { key: '/diamonds', label: '钻石流水', icon: <GoldOutlined />, to: '/diamonds' },
    ],
  },
  {
    key: 'grp-trade',
    label: '交易履约',
    icon: <ShoppingCartOutlined />,
    children: [
      { key: '/orders', label: '订单', icon: <ShoppingCartOutlined />, to: '/orders' },
      { key: '/benefit-orders', label: '权益订单', icon: <GiftOutlined />, to: '/benefit-orders' },
      { key: '/payments', label: '支付通道', icon: <CreditCardOutlined />, to: '/payments' },
      { key: '/fulfillments', label: '履约台账', icon: <DeploymentUnitOutlined />, to: '/fulfillments' },
    ],
  },
  {
    key: 'grp-growth',
    label: '用户与增长',
    icon: <UserOutlined />,
    children: [
      { key: '/users', label: '用户', icon: <UserOutlined />, to: '/users' },
      { key: '/ad-accounts', label: '投放账号', icon: <NotificationOutlined />, to: '/ad-accounts' },
      { key: '/attribution', label: '归因看板', icon: <FundViewOutlined />, to: '/attribution' },
    ],
  },
  {
    key: 'grp-platform',
    label: '平台与配置',
    icon: <SettingOutlined />,
    children: [
      { key: '/apps', label: 'App 配置', icon: <AppstoreOutlined />, to: '/apps' },
      { key: '/backends', label: 'GPU 算力池', icon: <CloudServerOutlined />, to: '/backends' },
      { key: '/integrations', label: '集成密钥', icon: <KeyOutlined />, to: '/integrations' },
      { key: '/tasks', label: '任务', icon: <FileImageOutlined />, to: '/tasks' },
      { key: '/settings', label: '账号设置', icon: <SettingOutlined />, to: '/settings' },
    ],
  },
];

// 扁平叶子列表 — selectedKey / breadcrumb 沿用原逻辑(按路径前缀匹配)。
const navLeaves: NavLeaf[] = navTree.flatMap((n) => (isGroup(n) ? n.children : [n]));

const groupKeys: string[] = navTree.filter(isGroup).map((g) => g.key);

const labelByPath: Record<string, string> = Object.fromEntries(
  navLeaves.map((n) => [n.key, n.label]),
);

export function AppLayout() {
  const { token } = theme.useToken();
  const location = useLocation();
  const navigate = useNavigate();
  const profile = useAuthStore((s) => s.profile);
  const clear = useAuthStore((s) => s.clear);

  // Pick the matching nav leaf by longest prefix — covers /apps/:id, etc.
  const selectedKey = useMemo(() => {
    const match = navLeaves
      .map((n) => n.key)
      .filter((k) => location.pathname.startsWith(k))
      .sort((a, b) => b.length - a.length)[0];
    return match || '/dashboard';
  }, [location.pathname]);

  const breadcrumb = useMemo(() => {
    const segments = location.pathname.split('/').filter(Boolean);
    const crumbs = [{ title: <Link to="/dashboard">首页</Link> }];
    let acc = '';
    for (const seg of segments) {
      acc += `/${seg}`;
      const label = labelByPath[acc] || decodeURIComponent(seg);
      crumbs.push({ title: <Link to={acc}>{label}</Link> });
    }
    return crumbs;
  }, [location.pathname]);

  const handleLogout = () => {
    clear();
    navigate('/login', { replace: true });
  };

  return (
    <AntLayout style={{ minHeight: '100vh' }}>
      <Sider
        breakpoint="lg"
        collapsedWidth={64}
        width={220}
        style={{ background: token.colorBgContainer, borderRight: `1px solid ${token.colorBorderSecondary}` }}
      >
        <div
          style={{
            height: 56,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: 18,
            color: token.colorPrimary,
            letterSpacing: 1,
          }}
        >
          touliu admin
        </div>
        <Menu
          mode="inline"
          selectedKeys={[selectedKey]}
          defaultOpenKeys={groupKeys}
          items={navTree.map((n) =>
            isGroup(n)
              ? {
                  key: n.key,
                  icon: n.icon,
                  label: n.label,
                  children: n.children.map((c) => ({
                    key: c.key,
                    icon: c.icon,
                    label: <Link to={c.to}>{c.label}</Link>,
                  })),
                }
              : {
                  key: n.key,
                  icon: n.icon,
                  label: <Link to={n.to}>{n.label}</Link>,
                },
          )}
        />
      </Sider>
      <AntLayout>
        <Header
          style={{
            background: token.colorBgContainer,
            padding: '0 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
          }}
        >
          <Breadcrumb items={breadcrumb} />
          <Dropdown
            menu={{
              items: [
                {
                  key: 'settings',
                  icon: <SettingOutlined />,
                  label: <Link to="/settings">账号设置</Link>,
                },
                { type: 'divider' },
                {
                  key: 'logout',
                  icon: <LogoutOutlined />,
                  label: '退出登录',
                  onClick: handleLogout,
                  danger: true,
                },
              ],
            }}
            placement="bottomRight"
          >
            <Space style={{ cursor: 'pointer' }}>
              <Avatar size="small" icon={<UserOutlined />} />
              <Typography.Text>
                {profile?.display_name || profile?.username || '管理员'}
              </Typography.Text>
            </Space>
          </Dropdown>
        </Header>
        <Content
          style={{
            margin: 16,
            padding: 24,
            background: token.colorBgContainer,
            borderRadius: 8,
            minHeight: 280,
          }}
        >
          <Outlet />
        </Content>
      </AntLayout>
    </AntLayout>
  );
}
